/**
 * Decision 0168: one status of the lead for every place that shows it (the header chip, the chat header, Home's project
 * card, Team), from the same facts the setup strip reads: a setup problem that blocks it (or `needs_attention`) is
 * "can't work"; else its newest turn failed (core `leadTurnFailed`) is "last turn failed"; else its presence. The online
 * dot shows only while it could work. Pure.
 */
import type { GlyphState } from "@/components/ui/status-dot"
import { leaStatusWord, leaWord } from "./glossary"
import { blocksLead, type SetupProblem, setupSentence } from "./setup"
import type { Agent } from "./view-types"

export interface LeadStatus {
  readonly key: "cant_work" | "failed" | "presence"
  /** The chip's lowercase word: "can't work", "last turn failed", "idle", "working". */
  readonly word: string
  /** The header's phrase: "Can't work", "Last turn failed", "Idle", "Working on DIC-6". */
  readonly phrase: string
  /** Home's line and the accessible sentence: "Lea can't work", "Lea's last turn failed", "Lea is idle". */
  readonly sentence: string
  readonly glyph: GlyphState
  /** Whether the lead's online dot may show (never next to "can't work" or "last turn failed"). */
  readonly dot: boolean
  /** Why, for a tooltip: the strip's sentence, the presence reason, or what failed. */
  readonly title?: string
}

/** "Jannik's", "Jess'". */
const possessive = (name: string): string => (name.endsWith("s") ? `${name}'` : `${name}'s`)

export const leadStatus = (input: {
  readonly name: string
  readonly state: Agent["state"]
  readonly reason?: string | null | undefined
  /** The viewer's setup problems (`setupProblems`, with their reach). */
  readonly problems: ReadonlyArray<SetupProblem>
  /** Items waiting for a runner (the strip's count, for the title). */
  readonly waiting?: number | undefined
  /** Core `leadTurnFailed` on the project page; Home's summary `lastTurnFailed`. */
  readonly lastTurnFailed?: boolean | undefined
  readonly workingOn?: string | null | undefined
}): LeadStatus => {
  const blocked = input.problems.some(blocksLead)
  if (blocked || input.state === "needs_attention")
    return {
      key: "cant_work",
      word: "can't work",
      phrase: "Can't work",
      sentence: `${input.name} can't work`,
      glyph: "blocked",
      dot: false,
      title: blocked
        ? setupSentence(input.problems, input.name, input.waiting ?? 0)
        : (input.reason ?? `${input.name} can't work`),
    }
  // a failed turn is said until the next one is asked (a retry is a new turn): never "idle" with an online dot
  if (input.lastTurnFailed === true && input.state === "idle")
    return {
      key: "failed",
      word: "last turn failed",
      phrase: "Last turn failed",
      sentence: `${possessive(input.name)} last turn failed`,
      glyph: "blocked",
      dot: false,
      title: `${possessive(input.name)} last turn failed: the chat says why and what happens next.`,
    }
  const word = leaWord(input.state)
  const phrase = leaStatusWord(input.state, input.workingOn ?? null)
  return {
    key: "presence",
    word: word.text,
    phrase,
    sentence: `${input.name} is ${phrase.slice(0, 1).toLowerCase()}${phrase.slice(1)}`,
    glyph: word.glyph,
    dot: true,
  }
}
